import React, { createContext, useEffect, useState, useMemo } from 'react';
import { useTheme } from 'styled-components';
import { Builder, builder, BuilderComponent, useIsPreviewing } from '@builder.io/react';
import _ from 'lodash';
import moment from 'moment';
import { DayHours, Dispensary } from '@dutchiesdk/ecommerce-extensions-sdk';

import { GqlProducts } from 'types/graphql';
import PublicEnv from 'shared/utils/public-env';
import { imageToUse } from 'shared/helpers/products';
import { replaceAWSSourceWithImgix } from 'shared/helpers/imgix';

import createContextHelpers from 'utils/create-context-helpers';
import { useBridgeDataInterface } from 'src/theme/provider/commerce-components/bridge/bridge-data-interface';
import useUI from 'src/hooks/use-ui';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useCart from 'src/hooks/use-cart';

import { registerCustomComponents } from './builder-registry';

// In-memory cache for Builder.IO content
export const builderContentCache = new Map<string, Promise<any>>();

// Generate cache key from model and options
const getCacheKey = (model: string, options: Record<string, any>, menuVariant: string): string =>
  `${model}:${menuVariant}:${JSON.stringify(options)}`;

/**
 * Builder.IO context configuration and client instance
 */
type BuilderContextValue = {
  builder: Builder;
  isInitialized: boolean;
  apiKey?: string;
};

const BuilderContext = createContext<BuilderContextValue | null>(null);

export const { useBuilder, withBuilder } = createContextHelpers(BuilderContext, 'builder');

/**
 * Provider component that initializes and provides Builder.IO client access
 */
type BuilderProviderProps = {
  children: React.ReactNode;
  apiKeyOverride?: string;
};

export const BuilderProvider: React.FC<BuilderProviderProps> = ({ children, apiKeyOverride }: BuilderProviderProps) => {
  const { dispensary } = useDispensary();

  // prioritize using `apiKeyOverride`, mostly for unit tests
  const apiKey = apiKeyOverride ?? dispensary?.remoteExtensions?.builderKey;

  const [isInitialized, setIsInitialized] = useState(false);
  const [builderApiKey, setBuilderApiKey] = useState<string | undefined>(apiKey);

  useEffect(() => {
    // Initialize Builder.IO with API key from props
    // Note: Add builderApiKey to PublicEnv if needed for environment variable access
    const key = apiKey;

    if (key) {
      // Always reinitialize if API key changes
      if (!isInitialized || builderApiKey !== key) {
        console.log('[builder] initializing builder with api key', apiKey);

        builder.init(key);
        setBuilderApiKey(key);
        setIsInitialized(true);
      }
    } else {
      // Reset initialization if no API key
      setIsInitialized(false);
      setBuilderApiKey(undefined);
    }
  }, [apiKey, builderApiKey, isInitialized]);

  const contextValue: BuilderContextValue = {
    builder,
    isInitialized,
    apiKey: builderApiKey,
  };

  return <BuilderContext.Provider value={contextValue}>{children}</BuilderContext.Provider>;
};

/**
 * Hook to access Builder.IO client and initialization status
 */
export const useBuilderClient = (): BuilderContextValue => {
  const context = useBuilder();

  if (!context) {
    console.error('useBuilderClient must be used within a BuilderProvider');
  }

  return context ?? {};
};

async function getBuilderContent(
  builderClient: Builder,
  model: BuilderModelsRegistry,
  options: any = {}
): Promise<any> {
  // Fetch from Builder.IO
  const result = await builderClient.get(model, options).promise();
  return result;
}

/**
 * Hook to get content from Builder.IO
 * @param model - The Builder.IO model name (e.g., 'page', 'section')
 * @param options - Query options for fetching content
 */
export const useBuilderContent = (
  model: BuilderModelsRegistry,
  options: any = {},
  skip?: boolean
): { content: any; loading: boolean; error: Error | null } => {
  const { builder: builderClient, isInitialized } = useBuilderClient();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const UI = useUI();
  const menuVariant = UI.variant;
  const cacheKey = getCacheKey(model, options, menuVariant);

  const bridgeData = useBridgeDataInterface();

  if (model === BuilderModelsRegistry.invalid) {
    skip = true;
  }

  useEffect(() => {
    // use isMounted to prevent async actions on unmounted component
    let isMounted = true;

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!builderClient) {
      setLoading(false);
      setError(new Error('No builder client found. Is there a BuilderProvider?'));

      return () => {
        isMounted = false;
      };
    }

    // we dont want to fetch new content when there's no location
    // but if we *had* good content before, then it's fine to keep using it until a new location is found and loaded
    // we don't use builder components on cross-enterprise-menus so this would at worst show content targetted at another location while loading
    // that's better than being in an error/loading/fallback state for longer than we need to be
    if (!bridgeData.location) {
      setLoading(false);
      setError(new Error('No store location found.'));

      return () => {
        isMounted = false;
      };
    }

    // if we're still loading the client, or told not to load, then do not fetch content
    // keep loading what it was before -- we want it to be in loading while initializing, and if we already loaded we don't want to go into loading
    // whatever the previous loading value was is the most correct value here, so a simple short circuit is ideal.
    if (!isInitialized || skip) {
      return () => {
        isMounted = false;
      };
    }

    void registerCustomComponents(bridgeData);

    const fetchContent = async (): Promise<void> => {
      try {
        // set loading to true since no matter what we're awaiting a promise at least
        setLoading(true);
        // unset error before doing anything
        setError(null);

        // Check cache first
        const cachedContentPromise = builderContentCache.get(cacheKey);

        if (cachedContentPromise) {
          const cachedContent = await cachedContentPromise;
          if (!isMounted) {
            return;
          }
          setContent(cachedContent);
          setLoading(false);
          return;
        }

        // Fetch from Builder.IO if not in cache
        const contentPromise = getBuilderContent(builderClient, model, {
          ...options,
          // cache bust after options, cache bust can't be set via options
          cacheBust: PublicEnv.appEnv === 'development',

          // spread options.userAttributes into userAttributes so that it works how you'd expect from the callsite
          userAttributes: {
            menuVariant,
            ...options.userAttributes,
          },
        });

        // Store the promise in cache, this prevents concurrent identical requests
        builderContentCache.set(cacheKey, contentPromise);

        // wait for data to load before setting content
        const result = await contentPromise;

        if (!isMounted) {
          return;
        }
        setContent(result);
      } catch (err) {
        // Remove failed promise from cache to allow retries
        builderContentCache.delete(cacheKey);
        if (!isMounted) {
          return;
        }
        setError(err instanceof Error ? err : new Error('Failed to fetch Builder.IO content'));
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    void fetchContent();

    // Cleanup function to prevent state updates after unmount
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, skip, cacheKey, menuVariant, !!bridgeData.location, isInitialized, builderClient]);

  return { content, loading, error };
};

export const useBuilderGlobalConfig = (): { content: any; loading: boolean; error: Error | null; config: any } => {
  const { content, loading, error } = useBuilderContent(BuilderModelsRegistry.globalConfig);
  return { content, loading, error, config: content?.data ?? {} };
};

export enum BuilderModelsRegistry {
  // Global Components
  siteHeader = 'site-header',
  siteFooter = 'site-footer',
  announcementBar = 'announcement-bar',
  // Pages
  page = 'page',
  // HomePage Sections
  homePageHero = 'home-page-hero',
  // Global Config
  globalConfig = 'global-site-config',
  productCard = 'product-card',

  invalid = '',
}

type UseBuilderComponentProps = {
  model: BuilderModelsRegistry;
  fetchOptions?: any;
  skip?: boolean;
  fallback?: React.FC | null;
};

type UseBuilderComponentReturn = {
  component: React.FC<any>;
  content: any;
  loading: boolean;
  error: Error | null;
  isPreviewingInBuilder: boolean;
};

function withDutchieBuilderComponent(
  // eslint-disable-next-line @typescript-eslint/naming-convention
  Component: React.FC,
  model: BuilderModelsRegistry,
  content: any
): React.FC<unknown> {
  return function WrappedBuilderComponent(props: any = {}): JSX.Element {
    return <Component model={model} content={content} {...props} />;
  };
}

function DutchieBuilderComponent(builderComponentProps: any = {}): JSX.Element {
  const theme = useTheme();
  const Cart = useCart();
  const orderType = Cart.orderType === 'pickup' ? `inStorePickup` : Cart.orderType;

  const { product, model, content, ...otherProps } = builderComponentProps;
  const bridgeData = useBridgeDataInterface();
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const cartItemCount = bridgeData.cart?.items?.length ?? 0;

  const mappedProduct = mapProductForBuilder(product);
  const mappedStoreData = mapStoreDataForBuilder(bridgeData.location, orderType);

  return (
    <BuilderComponent
      model={model}
      context={bridgeData}
      data={{
        theme,
        cart: { itemCount: cartItemCount, items: bridgeData.cart?.items },
        storeData: mappedStoreData,
        user: bridgeData.user,
        product: mappedProduct,
      }}
      content={content}
      {...otherProps}
    />
  );
}

export const useBuilderComponent = ({
  model,
  fetchOptions,
  skip,
  fallback,
}: UseBuilderComponentProps): UseBuilderComponentReturn => {
  const isPreviewingInBuilder = useIsPreviewing();

  if (model === BuilderModelsRegistry.invalid) {
    skip = true;
  }

  const { content, loading, error } = useBuilderContent(model, fetchOptions, skip);

  const component = useMemo((): React.FC<any> => {
    if (skip) {
      return fallback ?? _.constant(null);
    }
    if (loading) {
      return _.constant(null);
    }

    if (!content) {
      return fallback ?? _.constant(null);
    }

    return withDutchieBuilderComponent(DutchieBuilderComponent, model, content);
  }, [loading, model, content, fallback, skip]);

  return { component, content, loading, error, isPreviewingInBuilder };
};

function mapProductForBuilder(product?: GqlProducts): (GqlProducts & { Image: string }) | undefined {
  if (!product) {
    return undefined;
  }

  return {
    ...product,
    Image: replaceAWSSourceWithImgix(imageToUse(product)).replace(
      'https://s3-us-west-1.amazonaws.com/dutchie-images/',
      'https://images.dutchie.com/'
    ),
  };
}

type MappedDispensary = Dispensary & {
  hours: Dispensary['hours'] & {
    current: DayHours;
  };
};

export function mapStoreDataForBuilder(
  location: Dispensary | undefined,
  orderType: keyof Dispensary['hours'] | undefined
): MappedDispensary | undefined {
  if (!orderType) {
    orderType = 'inStorePickup';
  }
  if (!location) {
    return location;
  }

  const nowDayOfWeek = moment().format(`dddd`);

  return {
    ...location,
    hours: {
      ...location.hours,
      current: location.hours[orderType]?.effectiveHours?.[nowDayOfWeek],
    },
  };
}

export default BuilderContext;
